import * as React from 'react'
import { Form } from 'react-bootstrap'

import { copyObject, randomId } from '../../../../utils'
import InputDatePicker from '../../../../components/InputDatePicker'
import NumberFormatValidate from '../../../../components/NumberFormatValidate'
import { parseFloatTotal } from '../Job'
import { ExpenseLineItemT, JobT, VariableExpenseT } from '../../../../types/Job'
import { generateDamagesOptionsForCrew, isDamageItem, OptionsItemsExpense, parseDamageItem } from '../OptionsFieldsJob'

interface ExpenseLineItemProps {
    expenseLineItem: ExpenseLineItemT
    index: number
    job?: JobT
    removeLineItem(category: string, index: number): void
    updateLineItem(lineItemType: string, index: number, lineItem: ExpenseLineItemT): void
}



const ExpenseLineItem: React.FunctionComponent<ExpenseLineItemProps> = ({ expenseLineItem, index, job, removeLineItem, updateLineItem }): JSX.Element => {

    // Merge static options + dynamic damage options for each crew member
    const allOptions = React.useMemo<{ [k: string]: VariableExpenseT }>(() => {
        const damageOptions = job ? generateDamagesOptionsForCrew(job) : {}
        return { ...OptionsItemsExpense, ...damageOptions }
    }, [job?.crew])

    const updateExpenseLineItem = (key: string, value: any): void => {
        let expenseLineItemClone = copyObject(expenseLineItem)
        let isDirty = expenseLineItemClone.isDirty && expenseLineItemClone.isDirty == true
        if (!isDirty && key == "date") {
            expenseLineItemClone['isDirty'] = true
        }
        /**
        * ticket  Financial Issues
        *  Expenses -> `Processing Fee` units should be 1 and disabled
        */
        if (key == "item" && value == "processingFee") {
            expenseLineItemClone.units = 1
        }

        // When selecting a damage item, lock units to 1 and store workerId
        if (key == "item" && isDamageItem(value)) {
            expenseLineItemClone.units = 1
            const parsed = parseDamageItem(value)
            expenseLineItemClone.workerId = parsed ? parsed.workerId : null
        }

        // Clear workerId when switching away from a damage item
        if (key == "item" && !isDamageItem(value)) {
            expenseLineItemClone.workerId = null
        }

        expenseLineItemClone[key] = value
        if (key == 'rate' || key == 'units') expenseLineItemClone.total = expenseLineItemClone.rate * expenseLineItemClone.units
        updateLineItem('expenses', index, expenseLineItemClone)
    }


    const ItemsOptions = (): Array<JSX.Element> => {
        const staticKeys = Object.keys(OptionsItemsExpense)
        const damageKeys = job ? Object.keys(generateDamagesOptionsForCrew(job)) : []

        const staticOptions = staticKeys.map((li, i) => (
            <option value={li} key={`static-${i}`}>{OptionsItemsExpense[li].verboseName}</option>
        ))

        if (damageKeys.length === 0) return staticOptions

        return [
            ...staticOptions,
            <option disabled key="divider" value="">── Damages ──</option>,
            ...damageKeys.map((li, i) => (
                <option value={li} key={`damage-${i}`}>{allOptions[li].verboseName}</option>
            ))
        ]
    }

    /**
     * 
     * @param item 
     * @param isRate 
     */
    const renderLabel = (item: string, isRate: boolean = true): string => {
        if (allOptions[item]) {
            let label = `${reflectorLabel(allOptions[item], isRate)}`
            return label != "" ? isRate ? `${label}` : `, ${label}` : ""
        }
        if (!isRate) {
            return ", hours"
        }
        return `Rate, ${isRate ? '$/hour' : 'hours'}`;
    }
    /**
     * 
     * @param variable 
     * @param isRate 
     */
    const reflectorLabel = (variable: VariableExpenseT, isRate: boolean = true): string => {
        return isRate ? variable.rateLabel : variable.unitsLabel;
    }

    const isDamage = isDamageItem(expenseLineItem.item)

    return (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 30px' }}>
            <span>
                <Form.Label>Date</Form.Label>
                <InputDatePicker
                    id={`cogs-date-${index}`}
                    disabled={expenseLineItem?.paid == true}
                    className="input-material"
                    style={{ width: '100%' }}
                    selectedDate={`${expenseLineItem.date ? expenseLineItem.date : ""}`}
                    onChangeDate={(val) => {
                        updateExpenseLineItem('date', val)

                    }}
                    onChange={(val) => { }}
                    dateFormat="MM/dd/yyyy"
                    placeholder="Date"

                />
            </span>
            <span>
                <Form.Label>Item</Form.Label>
                <Form.Control
                    disabled={expenseLineItem?.paid == true}
                    as='select'
                    className='input-material'
                    onChange={(event: any): void => { updateExpenseLineItem('item', event.target.value) }}
                    value={`${expenseLineItem.item}`}>
                    <option value={''} ></option>
                    {ItemsOptions()}
                </Form.Control>

            </span>
            <span>
                <Form.Label>{renderLabel(expenseLineItem.item, true)}</Form.Label>
                <NumberFormatValidate
                    disabled={expenseLineItem?.paid == true}
                    customInput={Form.Control}
                    className="input-material"
                    type="text"
                    defaultValue={`${expenseLineItem.rate}`}
                    value={`${expenseLineItem.rate}`}
                    onValueChange={(values) => {
                        const { formattedValue, value, floatValue } = values;
                        if (floatValue != expenseLineItem.rate) {
                            updateExpenseLineItem('rate', floatValue)
                        }
                    }}
                    mask="_"
                    fixedDecimalScale={true}
                    decimalScale={2}
                />

            </span>
            <span>
                <Form.Label>Units{renderLabel(expenseLineItem.item, false)}</Form.Label>
                <NumberFormatValidate
                    // units locked to 1 for processingFee and damage items
                    disabled={expenseLineItem?.item == "processingFee" || isDamage}
                    customInput={Form.Control}
                    className="input-material"
                    type="text"
                    defaultValue={`${expenseLineItem.units}`}
                    value={`${expenseLineItem.units}`}
                    onValueChange={(values) => {
                        const { formattedValue, value, floatValue } = values;
                        if (floatValue != expenseLineItem.units) {
                            updateExpenseLineItem('units', floatValue)
                        }
                    }}
                    mask="_"
                    fixedDecimalScale={true}
                    decimalScale={2}

                />

            </span>
            <span>
                <Form.Label>Total, $</Form.Label>
                <Form.Control
                    className='input-material'
                    disabled
                    value={`${parseFloatTotal(expenseLineItem.total)}`} />
            </span>

            <div className="d-flex flex-row align-items-end" style={{ position: "relative", width: "100%", }}>

                <div style={{ cursor: 'pointer', position: "absolute", right: 1, top: 1 }} onClick={(): void => { removeLineItem('expenses', index) }}>x</div>


            </div>

        </div>
    )
}

export default ExpenseLineItem